/**
 * MailComposeMultiDialog — popup pre-invio unificato (spec 2026-07-02).
 * 1 bozza = espansa; N bozze = lista collassabile. L'utente rivede/edita e
 * preme un solo «Invia tutte (N)». Gli allegati sono read-only.
 */
import { useEffect, useRef, useState } from "react";

import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import type { MailDraft } from "./api";

const TITLE_ID = "mail-compose-multi-title";

export interface MailComposeMultiDialogProps {
  drafts: MailDraft[];
  title: string;
  onSendAll: (edited: MailDraft[]) => Promise<void> | void;
  onClose: () => void;
  sending?: boolean;
  sendError?: string | null;
  /** Fix review CRITICAL: ordini esclusi dal bulk preview (validation/not-found/
   *  LDV KO per-ordine) — senza questo, un invio parziale (es. 4/5 ordini)
   *  appare come un successo pieno. Stesso pattern di ``PackagesConfirmDialog``. */
  previewErrors?: { order_id: string; message: string }[];
}

export function MailComposeMultiDialog({
  drafts,
  title,
  onSendAll,
  onClose,
  sending = false,
  sendError = null,
  previewErrors = [],
}: MailComposeMultiDialogProps) {
  const [edited, setEdited] = useState<MailDraft[]>(() => drafts.map((d) => ({ ...d })));
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(drafts.length === 1 && drafts[0] ? [drafts[0].draft_key] : []),
  );
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !sending) onClose();
    };
    document.addEventListener("keydown", onKey, { capture: true });
    cancelRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey, { capture: true });
  }, [onClose, sending]);

  function patch(key: string, field: "recipient" | "subject" | "body", value: string) {
    setEdited((prev) => prev.map((d) => (d.draft_key === key ? { ...d, [field]: value } : d)));
  }

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Zero bozze è uno stato RAGGIUNGIBILE: il preview WF costruisce le bozze in
  // un try/except per-ordine e ritorna comunque `{preview, drafts: [], errors}`
  // se falliscono tutte (webfurniture_service.py), e il call-site apre il popup
  // senza guardare la lunghezza. Senza questa guardia il tasto restava acceso e
  // premuto mandava `mailOverrides = {}` a sendWebFurnitureOrderBulk: un invio
  // REALE sugli stessi ordini che l'anteprima aveva appena dichiarato falliti.
  const nessunaBozza = edited.length === 0;
  const sendLabel = sending
    ? "Invio in corso…"
    : nessunaBozza
      ? "Nessuna bozza da inviare"
      : edited.length === 1
        ? "Invia"
        : `Invia tutte (${edited.length})`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !sending) onClose();
      }}
    >
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-lg border border-line bg-card shadow-popover p-5">
        <h2 id={TITLE_ID} className="text-[15px] font-semibold text-ink mb-3">
          {title}
        </h2>

        {previewErrors.length > 0 && (
          <div role="alert" className="mb-3 rounded-sm border border-line bg-paper px-3 py-2">
            {previewErrors.map((err) => (
              <p key={err.order_id} className="text-[11.5px] text-red">
                Ordine {err.order_id}: {err.message} — escluso dall'invio
              </p>
            ))}
          </div>
        )}

        <div className="space-y-2">
          {edited.map((d) => {
            const isOpen = expanded.has(d.draft_key);
            return (
              <div key={d.draft_key} className="rounded-md border border-line">
                <button
                  type="button"
                  onClick={() => toggle(d.draft_key)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-paper"
                >
                  <span className="text-[12.5px] font-medium text-ink truncate">
                    {d.draft_key} — {d.subject}
                  </span>
                  <span className="text-[11px] text-ink-2 shrink-0">
                    {d.recipient} · {d.attachment_names.length} all.
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-line px-3 py-3 space-y-2.5">
                    <label className="block text-[11.5px] text-ink-2">
                      Destinatario
                      <input
                        aria-label="Destinatario"
                        value={d.recipient}
                        onChange={(e) => patch(d.draft_key, "recipient", e.target.value)}
                        className="mt-1 w-full rounded border border-line bg-card px-3 py-1.5 text-[13px] text-ink placeholder:text-ink-4 focus:outline-none focus:ring-1 focus:ring-accent"
                      />
                    </label>
                    <label className="block text-[11.5px] text-ink-2">
                      Oggetto
                      <input
                        aria-label="Oggetto"
                        value={d.subject}
                        onChange={(e) => patch(d.draft_key, "subject", e.target.value)}
                        className="mt-1 w-full rounded border border-line bg-card px-3 py-1.5 text-[13px] text-ink placeholder:text-ink-4 focus:outline-none focus:ring-1 focus:ring-accent"
                      />
                    </label>
                    <label className="block text-[11.5px] text-ink-2">
                      Corpo
                      <textarea
                        aria-label="Corpo"
                        value={d.body}
                        onChange={(e) => patch(d.draft_key, "body", e.target.value)}
                        rows={5}
                        className="mt-1 w-full rounded border border-line bg-card px-3 py-1.5 text-[13px] text-ink placeholder:text-ink-4 focus:outline-none focus:ring-1 focus:ring-accent resize-y"
                      />
                    </label>
                    {d.attachment_names.length > 0 && (
                      <ul className="space-y-1">
                        {d.attachment_names.map((name) => (
                          <li
                            key={name}
                            className="flex items-center gap-1.5 text-[12px] text-ink-2"
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                              className="text-ink-2 shrink-0"
                            >
                              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                            </svg>
                            <span>{name}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <ErrorBanner message={sendError} className="mt-3" />

        <div className="mt-4 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={sending}
            className="rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper disabled:cursor-not-allowed disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={() => void onSendAll(edited)}
            disabled={sending || nessunaBozza}
            aria-busy={sending}
            className="rounded-sm border border-accent bg-accent-2 px-[14px] py-2 text-[13px] font-medium text-white hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sendLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
